import { useEffect } from "react";

const SITE_NAME = "Mychromamind";

const OG_IMAGE_DEFAULT = "https://mychromamind.com/og-image.png";

/** Canonical host for the site. The app is also served live on www.mychromamind.com
 *  (for a working HTTPS experience there), but canonical/og:url must always point at
 *  the apex domain regardless of which host the visitor is actually on — otherwise
 *  visitors landing on www would get a self-referential canonical, defeating the
 *  whole point of having one and creating a duplicate-content signal for Google. */
const CANONICAL_ORIGIN = "https://mychromamind.com";

type SeoOptions = {
  title: string;
  description: string;
  /** Route-relative path (e.g. "/quiz/personality"). Do NOT pass window.location.pathname
   *  directly — useSeo prepends BASE_URL automatically to build the full canonical URL. */
  path: string;
  /** Open Graph page type. Defaults to "website"; pass "article" for content detail pages. */
  ogType?: "website" | "article";
  /** Absolute URL of the OG/Twitter share image. Falls back to the site-wide default. */
  ogImage?: string;
  /** Optional JSON-LD structured data object(s) to inject as <script type="application/ld+json">. */
  structuredData?: Record<string, unknown>;
  /** Set true for error/not-found states so search engines don't index them. Defaults to false. */
  noindex?: boolean;
};

function setMeta(nameOrProperty: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${nameOrProperty}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(nameOrProperty, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Lightweight per-page SEO manager for this client-rendered SPA: updates the
 * document title, meta description, canonical link, Open Graph / Twitter tags,
 * and (optionally) injects JSON-LD structured data while the page is mounted.
 * Reverts nothing on unmount since the next page immediately sets its own
 * values — avoids a flash of stale/blank meta between route changes.
 */
export function useSeo({
  title,
  description,
  path,
  ogType = "website",
  ogImage = OG_IMAGE_DEFAULT,
  structuredData,
  noindex = false,
}: SeoOptions) {
  useEffect(() => {
    document.title = title;

    setMeta("name", "description", description);
    // Always set explicitly so a noindex page's value never lingers on the next
    // indexable page (useSeo does not revert tags on unmount by design).
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width",  "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", SITE_NAME);

    // Twitter / X cards
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    // Canonical + og:url — always the apex origin (see CANONICAL_ORIGIN), never the
    // actual host the visitor is on. Path must be route-relative (no BASE_URL prefix).
    const base = import.meta.env.BASE_URL.replace(/\/$/, ""); // "" when mounted at "/"
    const url = `${CANONICAL_ORIGIN}${base}${path}`;

    setMeta("property", "og:url", url);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    let ld: HTMLScriptElement | null = null;
    if (structuredData) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.textContent = JSON.stringify(structuredData);
      document.head.appendChild(ld);
    }

    return () => {
      if (ld) ld.remove();
    };
  }, [title, description, path, ogType, ogImage, structuredData, noindex]);
}
